import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Button, IconButton, Typography, Chip, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Checkbox,
  Tooltip, FormControlLabel, Switch, Collapse, LinearProgress,
} from '@mui/material';
import {
  Close, Search, EditOutlined, DeleteOutline, WarningAmberRounded,
  CheckCircleOutline, EventBusy, TuneRounded, ArrowBack, ArrowForward,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { stopWheelNudge } from '../utils/numberInput';

/**
 * One workspace for bulk session edit + delete.
 *
 * Two panes rather than a wizard: a studio owner narrowing "the 7am slot in
 * September" iterates on the filter and watches the count move. A linear wizard
 * forces a restart on every tweak, which is what makes bulk tools feel risky.
 *
 * The invariant that makes this safe: the RIGHT pane is the only thing that can
 * be acted on. Preview returns ids, the action sends back exactly the ticked
 * ids, so what you saw is provably what you changed.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studioId: string;
}

interface PreviewSession {
  _id: string;
  istDate: string;
  istTime: string;
  className: string;
  slots: number;
  price: number;
  isLive: boolean;
  bookedCount: number;
  hasBookings: boolean;
}

const DAYS = [
  { label: 'S', full: 'Sunday', value: 0 },
  { label: 'M', full: 'Monday', value: 1 },
  { label: 'T', full: 'Tuesday', value: 2 },
  { label: 'W', full: 'Wednesday', value: 3 },
  { label: 'T', full: 'Thursday', value: 4 },
  { label: 'F', full: 'Friday', value: 5 },
  { label: 'S', full: 'Saturday', value: 6 },
];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const DATE_PRESETS = [
  { label: 'Next 30 days', get: () => [iso(new Date()), iso(addDays(new Date(), 30))] },
  { label: 'Next 3 months', get: () => [iso(new Date()), iso(addDays(new Date(), 90))] },
  { label: 'This month', get: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(new Date(n.getFullYear(), n.getMonth() + 1, 0))];
    } },
  { label: 'Next month', get: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth() + 1, 1)), iso(new Date(n.getFullYear(), n.getMonth() + 2, 0))];
    } },
];

const prettyDate = (s: string) =>
  new Date(s + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

const BulkSessionWorkspace: React.FC<Props> = ({ open, onClose, onSuccess, studioId }) => {
  const [classes, setClasses] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);

  // ── scope (left pane) ──
  const [classId, setClassId] = useState('');
  const [instructorFilter, setInstructorFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [times, setTimes] = useState<string[]>([]);
  const [trialOnly, setTrialOnly] = useState(false);
  const [includePast, setIncludePast] = useState(false);

  /**
   * Reaching into finished sessions is platform-team only — editing history
   * rewrites attendance and revenue reporting after the fact. The backend
   * rejects it for studio roles regardless of what is sent; hiding the control
   * just stops us offering something that would only ever fail.
   */
  const canIncludePast = !apiService.isStudio();

  // ── results (right pane) ──
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ total: number; withBookings: number; totalBookings: number; sessions: PreviewSession[] } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ── action ──
  const [mode, setMode] = useState<'none' | 'edit' | 'delete'>('none');
  const [chInstructor, setChInstructor] = useState('');
  const [chTime, setChTime] = useState('');
  const [chShift, setChShift] = useState('');
  const [chSlots, setChSlots] = useState('');
  const [chPrice, setChPrice] = useState('');
  const [chIsLive, setChIsLive] = useState<'' | 'true' | 'false'>('');
  const [chTrial, setChTrial] = useState<'' | 'true' | 'false'>('');
  const [chTrialPrice, setChTrialPrice] = useState('');
  // Opt-in, deliberately false to start — mirrors the single-instructor
  // delete/reassign flow's notifyMembers checkbox.
  const [chNotifyMembers, setChNotifyMembers] = useState(false);
  const [dryRun, setDryRun] = useState<any>(null);
  /** Server's answer to "what would this delete?" — see runDeleteDryRun. */
  const [delDry, setDelDry] = useState<{ matched: number; willDelete: number; skipped: string[] } | null>(null);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState<any>(null);

  const reset = useCallback(() => {
    setPreview(null); setSelected(new Set()); setMode('none');
    setDryRun(null); setDelDry(null); setDone(null); setError(null);
    setChInstructor(''); setChTime(''); setChShift(''); setChSlots(''); setChPrice(''); setChIsLive('');
    setChTrial(''); setChTrialPrice(''); setChNotifyMembers(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    // Scope (left pane) back to defaults. reset() deliberately leaves the scope
    // alone — "Back to results" calls it and must keep the search you ran — so
    // the scope is cleared here instead, only on a fresh open. Closing and
    // reopening the dialog therefore starts from scratch: no leftover time
    // filter, class or day selection from the previous session.
    setClassId(''); setInstructorFilter(''); setDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
    setTimes([]); setTrialOnly(false); setIncludePast(false);
    const [s, e] = DATE_PRESETS[0].get();
    setStartDate(s); setEndDate(e);
    (async () => {
      try {
        const [c, i] = await Promise.all([
          apiService.getStudioClasses(studioId),
          apiService.getInstructorsByStudio(studioId),
        ]);
        setClasses(c.classes || c.data || []);
        setInstructors(i.instructors || i.data || []);
      } catch { /* selectors stay empty; filters are all optional */ }
    })();
  }, [open, studioId, reset]);

  const filter = useMemo(() => ({
    ...(classId ? { class: classId } : {}),
    ...(instructorFilter ? { instructor: instructorFilter } : {}),
    startDate, endDate,
    ...(daysOfWeek.length < 7 ? { daysOfWeek } : {}),
    ...(times.length ? { times } : {}),
    ...(trialOnly ? { trialOnly: true } : {}),
    ...(includePast && canIncludePast ? { includePast: true } : {}),
  }), [classId, instructorFilter, startDate, endDate, daysOfWeek, times, trialOnly, includePast, canIncludePast]);

  const runPreview = async () => {
    if (!startDate || !endDate) { setError('Pick a start and end date'); return; }
    setLoading(true); setError(null); setDone(null); setMode('none'); setDryRun(null);
    try {
      const res = await apiService.bulkPreviewByRange(studioId, filter);
      setPreview(res);
      // Default to everything matched — the operator unticks exceptions, which
      // is far less error-prone than ticking 140 rows by hand.
      setSelected(new Set((res.sessions || []).map((s: PreviewSession) => s._id)));
    } catch (e: any) {
      setError(e?.message || 'Could not load sessions');
      setPreview(null);
    } finally { setLoading(false); }
  };

  /** Distinct start times in the result — lets you narrow to just one slot. */
  const availableTimes = useMemo(() => {
    if (!preview) return [];
    return Array.from(new Set(preview.sessions.map((s) => s.istTime))).sort();
  }, [preview]);

  /**
   * The chips to render for the time filter: every time in the current preview
   * PLUS any the operator has already picked.
   *
   * Once you narrow to a single slot and re-run, the preview only contains that
   * slot, so `availableTimes` collapses to one entry. Rendering off that alone
   * (and hiding the row when it has <2 entries) made the chips vanish the moment
   * a filter was applied — leaving `times` stuck in state with no control to
   * clear it, so the range looked permanently pinned to one time until a full
   * page reload reset the component. Keeping the picked times in the list means
   * the selection can always be un-picked.
   */
  const timeChoices = useMemo(() => {
    const set = new Set<string>(availableTimes);
    times.forEach((t) => set.add(t));
    return Array.from(set).sort();
  }, [availableTimes, times]);

  const grouped = useMemo(() => {
    if (!preview) return [];
    const m = new Map<string, PreviewSession[]>();
    preview.sessions.forEach((s) => { m.set(s.istDate, [...(m.get(s.istDate) || []), s]); });
    return Array.from(m.entries());
  }, [preview]);

  const selectedSessions = useMemo(
    () => (preview?.sessions || []).filter((s) => selected.has(s._id)),
    [preview, selected],
  );
  const selectedBooked = selectedSessions.filter((s) => s.hasBookings).length;

  /**
   * A checked impact belongs to the exact selection it was computed for. Untick
   * a row (or change a filter) and the number on the delete button would still
   * read the old total — which is precisely the class of lie this whole dialog
   * is being fixed for. Invalidate it and make the operator re-check.
   */
  useEffect(() => {
    setDelDry(null);
  }, [selected, classId, daysOfWeek, times, trialOnly, includePast]);

  const toggle = (id: string) => setSelected((p) => {
    const n = new Set(Array.from(p)); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const changes = useMemo(() => ({
    ...(chInstructor ? { instructor: chInstructor } : {}),
    ...(chTime ? { time: chTime } : {}),
    ...(chShift ? { shiftMinutes: Number(chShift) } : {}),
    ...(chSlots ? { slots: Number(chSlots) } : {}),
    // Regular (non-trial) price. '' means "leave each session's price alone";
    // '0' is a real value (a free session), so test against '' not falsiness.
    ...(chPrice !== '' ? { price: Number(chPrice) } : {}),
    ...(chIsLive ? { isLive: chIsLive === 'true' } : {}),
    ...(chTrial ? { isTrialEnabled: chTrial === 'true' } : {}),
    // Only meaningful alongside trials being on — the server rejects a price
    // sent while trials are being switched off.
    ...(chTrialPrice !== '' && chTrial !== 'false' ? { trialPrice: Number(chTrialPrice) } : {}),
  }), [chInstructor, chTime, chShift, chSlots, chPrice, chIsLive, chTrial, chTrialPrice]);

  const hasChanges = Object.keys(changes).length > 0;
  const retiming = !!(chTime || chShift);

  /**
   * What this session's time BECOMES, computed client-side purely so the rows
   * update as you type — the authoritative answer still comes from the server's
   * dry-run. Returns null when nothing moves, or when the shift would cross
   * midnight (the backend refuses those, so we must not promise them).
   *
   * Mirrors bulkUpdateByRange: booked sessions are never re-timed.
   */
  const projectedTime = useCallback((s: PreviewSession): string | null => {
    if (!retiming || s.hasBookings) return null;
    if (chTime) return chTime === s.istTime ? null : chTime;
    const delta = Number(chShift);
    if (!delta || Number.isNaN(delta)) return null;
    const [h, m] = s.istTime.split(':').map(Number);
    const total = h * 60 + m + delta;
    if (total < 0 || total >= 24 * 60) return null; // would leave the day
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }, [retiming, chTime, chShift]);

  const instructorName = useCallback(
    (id?: string | null) => instructors.find((i: any) => i._id === id)?.name || '—',
    [instructors],
  );

  const runDryRun = async () => {
    setApplying(true); setError(null);
    try {
      const res = await apiService.bulkUpdateByRange(studioId, {
        filter, changes, sessionIds: Array.from(selected), dryRun: true,
      });
      setDryRun(res);
    } catch (e: any) { setError(e?.message || 'Could not preview the change'); }
    finally { setApplying(false); }
  };

  const applyEdit = async () => {
    setApplying(true); setError(null);
    try {
      const res = await apiService.bulkUpdateByRange(studioId, {
        filter, changes, sessionIds: Array.from(selected), dryRun: false,
        // Ignored server-side when `changes.instructor` is absent.
        ...(chInstructor ? { notifyMembers: chNotifyMembers } : {}),
      });
      setDone({ kind: 'edit', ...res }); onSuccess();
    } catch (e: any) { setError(e?.message || 'Update failed'); }
    finally { setApplying(false); }
  };

  /**
   * The delete payloads — built ONCE and used by both the dry run and the real
   * delete, so the number on the button and the rows destroyed cannot drift.
   *
   * The delete endpoint is class-scoped, so a mixed-class selection becomes one
   * call per class.
   */
  const deleteRequests = useCallback(() => {
    const byClass = new Map<string, PreviewSession[]>();
    selectedSessions.forEach((s: any) => {
      byClass.set(s.class || classId, [...(byClass.get(s.class || classId) || []), s]);
    });
    return Array.from(byClass.entries()).map(([cls, list]) => {
      const dates = list.map((s) => s.istDate).sort();
      return {
        class: cls,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        ...(daysOfWeek.length < 7 ? { daysOfWeek } : {}),
        ...(times.length ? { times } : {}),
        ...(trialOnly ? { trialOnly: true } : {}),
        ...(includePast && canIncludePast ? { includePast: true } : {}),
        // THE line that matters. startDate/endDate above are only the span of
        // what was ticked; on their own they say "delete this class from the
        // first ticked date to the last", which is how 200 selected sessions
        // once destroyed 1691. These ids are what the operator actually saw
        // and chose — the backend re-applies the filter, so they can only
        // narrow the set. Never drop them from this call.
        sessionIds: list.map((s) => s._id),
      };
    });
  }, [selectedSessions, classId, daysOfWeek, times, trialOnly, includePast, canIncludePast]);

  /**
   * Ask the SERVER what this delete would actually do, before doing it.
   *
   * Non-negotiable: the confirm button used to read `Delete ${selected.size}` —
   * a client-side count of ticks that had no relationship to what the request
   * resolved to server-side. It said 200 while the backend destroyed 1691.
   * The count shown now comes back from the same endpoint, with the same
   * payload, that is about to run for real.
   */
  const runDeleteDryRun = async () => {
    setApplying(true); setError(null);
    try {
      let matched = 0, willDelete = 0; const skippedAll: string[] = [];
      for (const req of deleteRequests()) {
        const res = await apiService.bulkDeleteByRange(studioId, { ...req, dryRun: true });
        matched += res.matched ?? 0;
        willDelete += res.deletedCount ?? 0;
        skippedAll.push(...(res.skippedSessions || []));
      }
      setDelDry({ matched, willDelete, skipped: skippedAll });
    } catch (e: any) { setError(e?.message || 'Could not check what would be deleted'); }
    finally { setApplying(false); }
  };

  const applyDelete = async () => {
    setApplying(true); setError(null);
    try {
      let deleted = 0; const skippedAll: string[] = [];
      for (const req of deleteRequests()) {
        const res = await apiService.bulkDeleteByRange(studioId, { ...req, dryRun: false });
        deleted += res.deletedCount || 0;
        skippedAll.push(...(res.skippedSessions || []));
      }
      setDone({ kind: 'delete', deletedCount: deleted, skippedSessions: skippedAll });
      onSuccess();
    } catch (e: any) { setError(e?.message || 'Delete failed'); }
    finally { setApplying(false); }
  };

  // ────────────────────────────── render ──────────────────────────────

  const Stat = ({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) => (
    <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 2, backgroundColor: colors.backgroundTertiary }}>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: tone || colors.textPrimary, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>{label}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      slotProps={{ paper: { sx: { height: '90vh', borderRadius: 3, overflow: 'hidden' } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.75, borderBottom: `1px solid ${colors.divider}` }}>
        <TuneRounded sx={{ color: colors.primary, mr: 1.25 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Bulk sessions</Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
            Find a range, review what matched, then edit or delete
          </Typography>
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ── LEFT: scope ── */}
        <Box sx={{ width: 320, flexShrink: 0, borderRight: `1px solid ${colors.divider}`, overflowY: 'auto', p: 2.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, color: colors.textSecondary, mb: 1.5 }}>
            SCOPE
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {DATE_PRESETS.map((p) => (
              <Chip key={p.label} label={p.label} size="small" variant="outlined"
                onClick={() => { const [s, e] = p.get(); setStartDate(s); setEndDate(e); }}
                sx={{ fontSize: 11 }} />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField label="From" type="date" size="small" fullWidth value={startDate}
              onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" size="small" fullWidth value={endDate}
              onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Class</InputLabel>
            <Select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
              <MenuItem value=""><em>All classes</em></MenuItem>
              {classes.map((c: any) => <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Instructor</InputLabel>
            <Select label="Instructor" value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)}>
              <MenuItem value=""><em>Any instructor</em></MenuItem>
              {instructors.map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
            <Typography sx={{ fontSize: 12, color: colors.textSecondary, flex: 1 }}>Days</Typography>
            <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}
              disabled={daysOfWeek.length === 7}
              onClick={() => setDaysOfWeek([0, 1, 2, 3, 4, 5, 6])}>All</Button>
            <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}
              disabled={daysOfWeek.length === 0}
              onClick={() => setDaysOfWeek([])}>Clear</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mb: daysOfWeek.length === 0 ? 0.75 : 2 }}>
            {DAYS.map((d) => {
              const on = daysOfWeek.includes(d.value);
              return (
                <Tooltip key={d.value} title={d.full}>
                  <Box onClick={() => setDaysOfWeek((p) => on ? p.filter((x) => x !== d.value) : [...p, d.value])}
                    sx={{
                      width: 32, height: 32, borderRadius: '50%', display: 'grid', placeItems: 'center',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600, userSelect: 'none',
                      backgroundColor: on ? colors.primary : 'transparent',
                      color: on ? '#fff' : colors.textSecondary,
                      border: `1px solid ${on ? colors.primary : colors.divider}`,
                    }}>{d.label}</Box>
                </Tooltip>
              );
            })}
          </Box>

          {(timeChoices.length > 1 || times.length > 0) && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, flex: 1 }}>
                  Times in this range
                </Typography>
                {times.length > 0 && (
                  <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}
                    onClick={() => setTimes([])}>Clear</Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {timeChoices.map((t) => {
                  const on = times.includes(t);
                  return <Chip key={t} label={t} size="small" color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'} sx={{ fontSize: 11 }}
                    onClick={() => setTimes((p) => on ? p.filter((x) => x !== t) : [...p, t])} />;
                })}
              </Box>
              {times.length > 0 && (
                <Typography sx={{ fontSize: 11, color: colors.textSecondary, mt: -1.25, mb: 2 }}>
                  Re-run <strong>Find sessions</strong> to apply.
                </Typography>
              )}
            </>
          )}

          {daysOfWeek.length === 0 && (
            <Typography sx={{ fontSize: 11.5, color: colors.warning, mb: 2 }}>
              Pick at least one day to search.
            </Typography>
          )}

          {/* mb here (not only on the super-admin toggle below) — for a studio
              owner that toggle is absent, so without it the switch sits flush
              against the Find sessions button. */}
          <FormControlLabel sx={{ display: 'block', mb: 2 }}
            control={<Switch size="small" checked={trialOnly} onChange={(e) => setTrialOnly(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13 }}>Trial sessions only</Typography>} />
          {canIncludePast && (
            <FormControlLabel sx={{ display: 'block', mb: 2 }}
              control={<Switch size="small" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: 13 }}>Include past sessions</Typography>
                  <Chip size="small" label="Super admin"
                    sx={{ height: 17, fontSize: 9.5, fontWeight: 700, backgroundColor: `${colors.info}1F`, color: colors.info }} />
                </Box>
              } />
          )}

          <Button fullWidth variant="contained" startIcon={<Search />} onClick={runPreview}
            disabled={loading || daysOfWeek.length === 0}>
            {loading ? 'Finding…' : 'Find sessions'}
          </Button>
        </Box>

        {/* ── RIGHT: results + action ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {done ? (
            <Box sx={{ p: 4, textAlign: 'center', m: 'auto' }}>
              <CheckCircleOutline sx={{ fontSize: 56, color: colors.success }} />
              <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 1 }}>
                {done.kind === 'edit' ? `${done.updatedCount} session${done.updatedCount === 1 ? '' : 's'} updated` : `${done.deletedCount} session${done.deletedCount === 1 ? '' : 's'} deleted`}
              </Typography>
              {(done.skipped?.length || done.skippedSessions?.length) > 0 && (
                <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
                    {(done.skipped || done.skippedSessions).length} skipped
                  </Typography>
                  {(done.skipped || []).slice(0, 8).map((s: any, i: number) => (
                    <Typography key={i} sx={{ fontSize: 12 }}>{s.istDate} {s.istTime} — {s.reason}</Typography>
                  ))}
                  {(done.skippedSessions || []).slice(0, 8).map((s: any, i: number) => (
                    <Typography key={i} sx={{ fontSize: 12 }}>{s}</Typography>
                  ))}
                </Alert>
              )}
              <Button sx={{ mt: 3 }} variant="outlined" onClick={() => { reset(); runPreview(); }}>Back to results</Button>
            </Box>
          ) : !preview ? (
            <Box sx={{ m: 'auto', textAlign: 'center', color: colors.textSecondary, px: 4 }}>
              <EventBusy sx={{ fontSize: 48, opacity: 0.35 }} />
              <Typography sx={{ mt: 1.5, fontSize: 14 }}>
                Set a scope on the left, then <strong>Find sessions</strong>
              </Typography>
              <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                Nothing is changed until you review what matched.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, p: 2, pb: 1.5 }}>
                <Stat label="matched" value={preview.total} />
                <Stat label="have bookings" value={preview.withBookings} tone={preview.withBookings ? colors.warning : undefined} />
                <Stat label="members booked" value={preview.totalBookings} />
                <Stat label="selected" value={selected.size} tone={colors.primary} />
              </Box>

              {preview.total === 0 ? (
                <Alert severity="info" sx={{ mx: 2 }}>
                  No sessions matched. Widen the date range{canIncludePast ? <>, or turn on <strong>Include past sessions</strong></> : ''}.
                </Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1 }}>
                    <Button size="small" onClick={() => setSelected(new Set(preview.sessions.map((s) => s._id)))}>All</Button>
                    <Button size="small" onClick={() => setSelected(new Set())}>None</Button>
                    <Button size="small" onClick={() => setSelected(new Set(preview.sessions.filter((s) => !s.hasBookings).map((s) => s._id)))}>
                      Only unbooked
                    </Button>
                  </Box>

                  <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
                    {grouped.map(([date, list]) => (
                      <Box key={date} sx={{ mb: 1.25 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.textSecondary, mb: 0.5 }}>
                          {prettyDate(date)}
                        </Typography>
                        {list.map((s) => {
                          const on = selected.has(s._id);
                          return (
                            <Box key={s._id} onClick={() => toggle(s._id)}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, mb: 0.5,
                                borderRadius: 1.5, cursor: 'pointer',
                                border: `1px solid ${on ? colors.primary : colors.divider}`,
                                backgroundColor: on ? `${colors.primary}0F` : s.hasBookings ? `${colors.warning}0A` : 'transparent',
                              }}>
                              <Checkbox size="small" checked={on} sx={{ p: 0.25 }} />
                              {(() => {
                                const to = on && mode === 'edit' ? projectedTime(s) : null;
                                return to ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 118 }}>
                                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, textDecoration: 'line-through' }}>
                                      {s.istTime}
                                    </Typography>
                                    <ArrowForward sx={{ fontSize: 13, color: colors.primary }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: colors.primary }}>{to}</Typography>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: 13, fontWeight: 700, width: 52 }}>{s.istTime}</Typography>
                                );
                              })()}
                              <Typography sx={{ fontSize: 13, flex: 1 }} noWrap>{s.className}</Typography>
                              {on && mode === 'edit' && chSlots && Number(chSlots) !== s.slots ? (
                                <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                  {s.slots} → {chSlots} slots
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: 11.5, color: colors.textSecondary }}>{s.slots} slots</Typography>
                              )}
                              {on && mode === 'edit' && chPrice !== '' && Number(chPrice) !== s.price && (
                                <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                  ₹{s.price} → ₹{chPrice}
                                </Typography>
                              )}
                              {on && mode === 'edit' && retiming && s.hasBookings && (
                                <Tooltip title="Time not changed — this session has bookings">
                                  <Chip size="small" label="time kept"
                                    sx={{ height: 20, fontSize: 10.5, backgroundColor: `${colors.warning}22`, color: '#8a5a00' }} />
                                </Tooltip>
                              )}
                              {s.hasBookings && (
                                <Chip size="small" label={`${s.bookedCount} booked`}
                                  sx={{ height: 20, fontSize: 10.5, backgroundColor: `${colors.warning}22`, color: '#8a5a00' }} />
                              )}
                              {!s.isLive && <Chip size="small" label="hidden" sx={{ height: 20, fontSize: 10.5 }} />}
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>

                  {/* action bar */}
                  <Box sx={{ borderTop: `1px solid ${colors.divider}`, p: 2 }}>
                    <Collapse in={mode === 'edit'}>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                          <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>New instructor</InputLabel>
                            <Select label="New instructor" value={chInstructor} onChange={(e) => setChInstructor(e.target.value)}>
                              <MenuItem value=""><em>Unchanged</em></MenuItem>
                              {/* A freshly-picked target, not tied to any one session's
                                  current value — unlike the single-session edit dialog,
                                  there is no "current selection" to keep visible here. */}
                              {instructors.filter((i: any) => !i.isDisabled).map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                          <TextField label="New time" type="time" size="small" sx={{ width: 130 }}
                            value={chTime} disabled={!!chShift}
                            onChange={(e) => setChTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                          <TextField label="Shift (min)" type="number" size="small" sx={{ width: 110 }}
                            value={chShift} disabled={!!chTime}
                            onWheel={stopWheelNudge}
                            onChange={(e) => setChShift(e.target.value)} InputLabelProps={{ shrink: true }} />
                          <TextField label="Slots" type="number" size="small" sx={{ width: 90 }}
                            value={chSlots} onWheel={stopWheelNudge}
                            onChange={(e) => setChSlots(e.target.value)} InputLabelProps={{ shrink: true }} />
                          <TextField label="Session price" type="number" size="small" sx={{ width: 120 }}
                            value={chPrice} onChange={(e) => setChPrice(e.target.value)}
                            onWheel={stopWheelNudge}
                            InputLabelProps={{ shrink: true }} />
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Visibility</InputLabel>
                            <Select label="Visibility" value={chIsLive} onChange={(e) => setChIsLive(e.target.value as any)}>
                              <MenuItem value=""><em>Unchanged</em></MenuItem>
                              <MenuItem value="true">Visible</MenuItem>
                              <MenuItem value="false">Hidden</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Trial</InputLabel>
                            <Select label="Trial" value={chTrial}
                              onChange={(e) => {
                                const v = e.target.value as '' | 'true' | 'false';
                                setChTrial(v);
                                // Turning trials off makes a price meaningless.
                                if (v === 'false') setChTrialPrice('');
                              }}>
                              <MenuItem value=""><em>Unchanged</em></MenuItem>
                              <MenuItem value="true">Enable trial</MenuItem>
                              <MenuItem value="false">Disable trial</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField label="Trial price" type="number" size="small" sx={{ width: 120 }}
                            value={chTrialPrice} disabled={chTrial === 'false'}
                            onWheel={stopWheelNudge}
                            onChange={(e) => setChTrialPrice(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText={chTrial === 'true' && chTrialPrice === '' ? 'Keeps each session\u2019s current price' : ' '} />
                        </Box>

                        {retiming && selectedBooked > 0 && (
                          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 1.5 }}>
                            <strong>{selectedBooked}</strong> of the selected sessions already have bookings.
                            Their <strong>time will not change</strong> — members were told a time and there is no
                            "class moved" notification. They'll be listed as skipped. Instructor and visibility changes still apply to them.
                          </Alert>
                        )}

                        {/* Unlike a retime, an instructor swap is never blocked by
                            existing bookings — it just silently changes who shows up.
                            Same courtesy the single-instructor delete/reassign flow
                            gives, opt-in and off by default. */}
                        {chInstructor && selectedBooked > 0 && (
                          <FormControlLabel
                            sx={{ mb: 1.5, display: 'flex' }}
                            control={
                              <Checkbox
                                size="small"
                                checked={chNotifyMembers}
                                onChange={(e) => setChNotifyMembers(e.target.checked)}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: 13 }}>
                                Tell <strong>{selectedBooked}</strong> affected {selectedBooked === 1 ? 'member' : 'members'} their instructor changed
                              </Typography>
                            }
                          />
                        )}

                        {dryRun && (
                          <Box sx={{ mb: 1.5, border: `1px solid ${colors.divider}`, borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, backgroundColor: colors.backgroundTertiary }}>
                              <CheckCircleOutline sx={{ fontSize: 18, color: dryRun.updatedCount ? colors.success : colors.textSecondary }} />
                              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                {dryRun.updatedCount} will change
                              </Typography>
                              {dryRun.skipped?.length > 0 && (
                                <Chip size="small" label={`${dryRun.skipped.length} skipped`}
                                  sx={{ height: 20, fontSize: 10.5, backgroundColor: `${colors.warning}22`, color: '#8a5a00' }} />
                              )}
                            </Box>

                            <Box sx={{ maxHeight: 148, overflowY: 'auto', px: 1.5, py: 1 }}>
                              {(dryRun.preview || []).map((d: any) => (
                                <Box key={d._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
                                  <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, width: 74 }}>
                                    {prettyDate(d.istDate)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, flex: 1 }} noWrap>{d.className}</Typography>
                                  {d.time && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                      <Typography sx={{ fontSize: 12, color: colors.textSecondary, textDecoration: 'line-through' }}>{d.time.from}</Typography>
                                      <ArrowForward sx={{ fontSize: 12, color: colors.primary }} />
                                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: colors.primary }}>{d.time.to}</Typography>
                                    </Box>
                                  )}
                                  {d.slots && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                      {d.slots.from} → {d.slots.to} slots
                                    </Typography>
                                  )}
                                  {d.price && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                      ₹{d.price.from} → ₹{d.price.to}
                                    </Typography>
                                  )}
                                  {d.instructor && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }} noWrap>
                                      {instructorName(d.instructor.from)} → {instructorName(d.instructor.to)}
                                    </Typography>
                                  )}
                                  {d.isLive && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                      {d.isLive.from ? 'Visible' : 'Hidden'} → {d.isLive.to ? 'Visible' : 'Hidden'}
                                    </Typography>
                                  )}
                                  {d.isTrialEnabled && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                      Trial {d.isTrialEnabled.from ? 'on' : 'off'} → {d.isTrialEnabled.to ? 'on' : 'off'}
                                    </Typography>
                                  )}
                                  {d.trialPrice && (
                                    <Typography sx={{ fontSize: 11.5, color: colors.primary, fontWeight: 700 }}>
                                      ₹{d.trialPrice.from} → ₹{d.trialPrice.to}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                              {dryRun.previewTruncated && (
                                <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, pt: 0.5 }}>
                                  …and more (showing the first {dryRun.preview.length})
                                </Typography>
                              )}
                              {(dryRun.skipped || []).map((s: any, i: number) => (
                                <Box key={`sk${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35, opacity: 0.75 }}>
                                  <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, width: 74 }}>{prettyDate(s.istDate)}</Typography>
                                  <Typography sx={{ fontSize: 12, width: 46 }}>{s.istTime}</Typography>
                                  <Typography sx={{ fontSize: 11.5, color: '#8a5a00', flex: 1 }} noWrap>{s.reason}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Collapse>

                    <Collapse in={mode === 'delete'}>
                      {!delDry ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          You have <strong>{selected.size}</strong> session{selected.size === 1 ? '' : 's'} ticked.
                          Check the impact to see what the server will actually delete before committing.
                        </Alert>
                      ) : (
                        <Alert severity={delDry.willDelete === selected.size ? 'error' : 'warning'} sx={{ mb: 2 }}>
                          The server will delete <strong>{delDry.willDelete}</strong> session
                          {delDry.willDelete === 1 ? '' : 's'}. This cannot be undone.
                          {delDry.skipped.length > 0 && (
                            <> <strong>{delDry.skipped.length}</strong> matched but have bookings and will be skipped.</>
                          )}
                          {delDry.willDelete !== selected.size && (
                            <>
                              {' '}<strong>
                                This does not match your {selected.size} ticked session
                                {selected.size === 1 ? '' : 's'} — do not proceed unless you understand why.
                              </strong>
                            </>
                          )}
                        </Alert>
                      )}
                    </Collapse>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 13, color: colors.textSecondary, flex: 1 }}>
                        {selected.size} selected{selectedBooked > 0 && ` · ${selectedBooked} with bookings`}
                      </Typography>

                      {mode === 'none' && (
                        <>
                          <Button startIcon={<DeleteOutline />} color="error" disabled={!selected.size}
                            onClick={() => setMode('delete')}>Delete</Button>
                          <Button startIcon={<EditOutlined />} variant="contained" disabled={!selected.size}
                            onClick={() => setMode('edit')}>Edit</Button>
                        </>
                      )}

                      {mode === 'edit' && (
                        <>
                          <Button startIcon={<ArrowBack />} onClick={() => { setMode('none'); setDryRun(null); }}>Back</Button>
                          {!dryRun ? (
                            <Button variant="contained" disabled={!hasChanges || applying} onClick={runDryRun}>
                              {applying ? <CircularProgress size={18} /> : 'Preview change'}
                            </Button>
                          ) : (
                            <Button variant="contained" color="primary" disabled={applying || !dryRun.updatedCount}
                              onClick={applyEdit}>
                              {applying ? <CircularProgress size={18} /> : `Apply to ${dryRun.updatedCount}`}
                            </Button>
                          )}
                        </>
                      )}

                      {mode === 'delete' && (
                        <>
                          <Button startIcon={<ArrowBack />} onClick={() => { setMode('none'); setDelDry(null); }}>Back</Button>
                          {!delDry ? (
                            <Button variant="contained" color="warning" disabled={applying || !selected.size}
                              onClick={runDeleteDryRun}>
                              {applying ? <CircularProgress size={18} /> : 'Check impact'}
                            </Button>
                          ) : (
                            // The count is the SERVER's, from the same payload that
                            // is about to run for real — never selected.size.
                            <Button variant="contained" color="error" disabled={applying || !delDry.willDelete}
                              onClick={applyDelete}>
                              {applying ? <CircularProgress size={18} /> : `Delete ${delDry.willDelete}`}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkSessionWorkspace;
